import { FC, useState, useRef, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface FeatureNotAvailableProps {
  isOpen: boolean;
  onClose: () => void;
  position: { x: number; y: number };
}

export const FeatureNotAvailablePopup: FC<FeatureNotAvailableProps> = ({ isOpen, onClose, position }) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  useEffect(() => {
    if (isOpen && popupRef.current) {
      const popup = popupRef.current;
      const rect = popup.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newX = position.x;
      let newY = position.y;

      // Adjust horizontal position if popup goes off-screen
      if (position.x + rect.width > viewportWidth - 20) {
        newX = viewportWidth - rect.width - 20;
      }
      if (newX < 20) {
        newX = 20;
      }

      // Adjust vertical position if popup goes off-screen
      if (position.y + rect.height > viewportHeight - 20) {
        newY = position.y - rect.height - 10;
      }
      if (newY < 20) {
        newY = 20;
      }

      setAdjustedPosition({ x: newX, y: newY });
    }
  }, [isOpen, position]);

  if (!isOpen) return null;

  return (
    <div 
      ref={popupRef}
      className="fixed z-[100] animate-fade-in"
      style={{ 
        left: adjustedPosition.x, 
        top: adjustedPosition.y,
      }}
    >
      <div className="relative flex items-center gap-4 px-6 py-4 rounded-2xl bg-[#182233]/90 backdrop-blur-md border border-gray-700/50 shadow-2xl shadow-black/50 min-w-[320px] max-w-[450px]">
        {/* Warning Icon */}
        <div className="flex-shrink-0">
          <div className="relative">
            <AlertTriangle className="w-12 h-12 text-white fill-[#182233] stroke-[#2777EF]" strokeWidth={2} />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white text-lg font-bold">!</span>
            </div>
          </div>
        </div>

        {/* Message */}
        <div className="flex-1 pr-6">
          <p className="text-white text-base font-medium leading-relaxed">
            You don't have access to this feature yet!
          </p>
          <p className="text-white text-base">
            <a 
              href="https://loopchain.io/contact" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#2777EF] font-semibold underline hover:text-[#4d94ff] transition-colors"
            >
              Contact us
            </a>
            {' '}to unlock everything
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center rounded-full bg-[#2777EF] text-white hover:bg-[#0053E2] transition-colors shadow-lg"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Custom hook for managing the popup
export const useFeatureNotAvailable = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const showPopup = (event: React.MouseEvent) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    setPosition({
      x: rect.left,
      y: rect.bottom + 10
    });
    setIsOpen(true);
  };

  const hidePopup = () => {
    setIsOpen(false);
  };

  return {
    isOpen,
    position,
    showPopup,
    hidePopup
  };
};

export default FeatureNotAvailablePopup;

